import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useGroups } from "@nju/data-access-portal-dashboard";
import { useProducts } from "@nju/data-access-portal-product";
import { useUser } from "@nju/data-access-portal-user";
import { pushSimpleEvent } from "@nju/data-layer";
import { GeneralErrorNotification } from "@nju/ui";
import { cutPhoneNumberPrefix } from "@nju/utils";
import { ConfirmDisagreement } from "./partials/ConfirmDisagreement";
import { ConfirmDisagreementSuccess } from "./partials/ConfirmDisagreementSuccess";
import { ConsentsInfo } from "./partials/ConsentsInfo";
import { LoginInfo } from "./partials/LoginInfo";
import { UserInfo } from "./partials/UserInfo";
import { userProfileMachine } from "./userProfile.machine";
import { isServiceInactive } from "./utils";

export function DashboardUserProfilePage() {
  const [isConsentsCardVisible, setIsConsentsCardVisible] =
    React.useState(false);

  const router = useRouter();
  const {
    firstName,
    lastName,
    email,
    attributes,
    isEmailVerified,
    isPersonVerified,
  } = useUser();
  const { ftthProducts, mobileProducts } = useProducts();
  const groups = useGroups();

  const contactNumber = cutPhoneNumberPrefix(
    attributes.contactNumber?.toString().trim(),
  );

  const [current, send] = useMachine(userProfileMachine, {
    context: {
      currentAgreement: attributes.contactChannels[0],
      allowSmsContact: !attributes.contactChannels[0].includes("sms"),
    },
  });

  const { allowSmsContact, errorMessage } = current.context;

  React.useEffect(() => {
    const isAnyServiceActive = [
      ftthProducts.access?.status,
      mobileProducts.mobileAccess?.status,
    ].some((status) => {
      return !isServiceInactive(status);
    });
    const isAnyGroupMemberActive = !!groups?.[0]?.members.some((member) => {
      return member.primaryMsisdn;
    });

    setIsConsentsCardVisible(isAnyServiceActive || isAnyGroupMemberActive);
  }, [
    ftthProducts.access?.status,
    mobileProducts.mobileAccess?.status,
    groups,
  ]);

  const toggleAgreement = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_contact_resignation",
      eventName: "contact_resignation_order",
    });
    send({
      type: "TOGGLE_AGREEMENT",
    });
  }, [send]);

  const refresh = React.useCallback(() => {
    router.reload();
  }, [router]);

  const handleConfirmDisagreement = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_contact_resignation",
      eventName: "contact_resignation_confirmed",
    });
    send({
      type: "CONFIRM_SET_DISAGREEMENT",
    });
  }, [send]);

  if (
    current.matches("disagreement") ||
    current.matches("confirmDisagreement")
  ) {
    return (
      <ConfirmDisagreement
        backToProfile={refresh}
        confirmDisagreement={handleConfirmDisagreement}
        isLoading={current.matches("confirmDisagreement")}
      />
    );
  }

  if (current.matches("disagreementSuccess")) {
    return <ConfirmDisagreementSuccess refresh={refresh} />;
  }

  if (current.matches("disagreementError")) {
    return <GeneralErrorNotification callback={refresh} />;
  }

  return (
    <div className="grid gap-4 from-desktop:grid-cols-2 items-start">
      {isPersonVerified && (
        <UserInfo
          contactNumber={contactNumber}
          errorMessage={errorMessage}
          firstName={firstName}
          isDisagreementSelected={allowSmsContact}
          lastName={lastName}
          toggleAgreement={toggleAgreement}
        />
      )}
      <div className="flex flex-col gap-4">
        <LoginInfo email={email} isEmailVerified={isEmailVerified} />
        {isConsentsCardVisible && <ConsentsInfo />}
      </div>
    </div>
  );
}
